import { ColumnVisibilityItem } from "providers/Auth";
import { type SortingState } from "@tanstack/react-table";

export const LEADS_COLUMNS_TAB_KEY = "leads";

export const DEFAULT_PER_PAGE = window.localStorage.getItem("per_page") || "10";

export const SESSION_STORAGE_KEY = "leads_table_query_params";

// Saved-views mode keeps column visibility as ephemeral working state (not
// persisted to the user record). We still mirror it to sessionStorage so the
// full working state — filters (URL) + columns — survives a page reload and the
// active saved view can be recovered instead of falling back to "Custom".
export const SESSION_STORAGE_COLUMNS_KEY = "leads_table_columns_visibility";

export const BASE_URL_PARAM_KEYS = [
  "search",
  "page",
  "per_page",
  "starred"
] as const;

// Only the global /leads page binds these to the URL — on tabbed routes
// (property/unit/contact) they would collide with the applications table,
// which manages the same "properties"/"assignee_ids"/"sort_by"/"sort_dir" keys
const FILTER_URL_PARAM_KEYS = [
  "properties",
  "stages",
  "lead_sources",
  "lead_types",
  "activity_types",
  "created_within",
  "created_after",
  "created_before",
  "assignee_ids",
  "sort_by",
  "sort_dir"
] as const;

export const URL_PARAM_KEYS = [
  ...BASE_URL_PARAM_KEYS,
  ...FILTER_URL_PARAM_KEYS,
  "view"
] as const;

export const DEFAULT_URL_PARAMS: Record<string, string> = Object.fromEntries(
  URL_PARAM_KEYS.map((key) => [key, ""])
);

export const BASE_DEFAULT_URL_PARAMS: Record<string, string> =
  Object.fromEntries(BASE_URL_PARAM_KEYS.map((key) => [key, ""]));

export const LEADS_COLUMNS_CONTEXT_LABEL = "Leads";

export enum LeadView {
  List = "list",
  Pipeline = "pipeline"
}

export enum LeadColumnId {
  Name = "name",
  PropertyInterested = "property_interested",
  UnitInterested = "unit_interested",
  Type = "lead_type",
  Source = "source",
  LastNote = "last_note",
  RecentActivity = "recent_activity",
  AssignedTo = "assigned_to",
  StageSelector = "stage_selector"
}

export const DEFAULT_SORTING: SortingState = [
  { id: LeadColumnId.RecentActivity, desc: true }
];

export enum LeadSortColumnId {
  LastNote = "last_note",
  RecentActivity = "recent_activity"
}

export const LEAD_SORT_COLUMN_MAP: Record<LeadSortColumnId, string> = {
  [LeadSortColumnId.LastNote]: "last_note_at",
  [LeadSortColumnId.RecentActivity]: "most_recent_activity"
};

export const LEADS_COLUMN_ORDER: LeadColumnId[] = [
  LeadColumnId.Name,
  LeadColumnId.PropertyInterested,
  LeadColumnId.UnitInterested,
  LeadColumnId.Type,
  LeadColumnId.Source,
  LeadColumnId.LastNote,
  LeadColumnId.RecentActivity,
  LeadColumnId.AssignedTo,
  LeadColumnId.StageSelector
];

const LEADS_DEFAULT_COLUMN_VISIBILITY: ColumnVisibilityItem[] = [
  {
    id: LeadColumnId.Name,
    label: "Name",
    checked: true,
    disabled: false
  },
  {
    id: LeadColumnId.PropertyInterested,
    label: "Property interested",
    checked: true,
    disabled: false
  },
  {
    id: LeadColumnId.UnitInterested,
    label: "Unit interested",
    checked: true,
    disabled: false
  },
  {
    id: LeadColumnId.Source,
    label: "Source",
    checked: true,
    disabled: false
  },
  {
    id: LeadColumnId.LastNote,
    label: "Last note",
    checked: true,
    disabled: false
  },
  {
    id: LeadColumnId.RecentActivity,
    label: "Most recent activity",
    checked: true,
    disabled: false
  },
  {
    id: LeadColumnId.AssignedTo,
    label: "Assigned to",
    checked: true,
    disabled: false
  },
  {
    id: LeadColumnId.StageSelector,
    label: "Current stage",
    checked: true,
    disabled: true
  }
];

// The Type column only exists for owner-leads partners, so it's added to the
// default visibility (and thus the column-visibility panel + saved views) only
// when the feature is enabled. Inserted before Source to match LEADS_COLUMN_ORDER.
const LEADS_TYPE_COLUMN_VISIBILITY: ColumnVisibilityItem = {
  id: LeadColumnId.Type,
  label: "Type",
  checked: true,
  disabled: false
};

export const getLeadsDefaultColumnVisibility = (
  isOwnerLeadsEnabled: boolean
): ColumnVisibilityItem[] => {
  if (!isOwnerLeadsEnabled) return LEADS_DEFAULT_COLUMN_VISIBILITY;
  const columns = [...LEADS_DEFAULT_COLUMN_VISIBILITY];
  const sourceIndex = columns.findIndex(
    (col) => col.id === LeadColumnId.Source
  );
  columns.splice(
    sourceIndex === -1 ? columns.length : sourceIndex,
    0,
    LEADS_TYPE_COLUMN_VISIBILITY
  );
  return columns;
};
